import { useEffect, useState, useCallback } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Rss, Users, MessageSquare, CalendarDays, BookUser, Lock, Globe, Plus, Video } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";

export function useCommunity() {
  const [me, setMe] = useState(null);
  const refresh = useCallback(() => api.get("/community/me").then((r) => setMe(r.data)).catch(() => setMe({ groups: [], roles: [], unread_messages: 0 })), []);
  useEffect(() => { refresh(); }, [refresh]);
  return { me, refresh };
}

const NAV = [
  { to: "/community", label: "Community feed", icon: Rss, end: true, id: "feed" },
  { to: "/community/groups", label: "My groups", icon: Users, id: "groups" },
  { to: "/community/messages", label: "Messages", icon: MessageSquare, id: "messages" },
  { to: "/community/events", label: "Events", icon: CalendarDays, id: "events" },
  { to: "/community/members", label: "Member directory", icon: BookUser, id: "members" },
  { to: "/community/live", label: "Voice & Video", icon: Video, id: "live" },
];

function SideNav({ me }) {
  const link = ({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-[#1a2234] text-white" : "text-slate-400 hover:text-white hover:bg-[#131722]"}`;
  const groups = me?.groups || [];
  const [live, setLive] = useState({});
  useEffect(() => { const f = () => api.get("/calls/rooms").then((r) => setLive(Object.fromEntries(r.data.map((x) => [x.slug, x.live])))).catch(() => {}); f(); const t = setInterval(f, 10000); return () => clearInterval(t); }, []);
  const liveTotal = Object.values(live).reduce((a, b) => a + b, 0);
  return (
    <aside className="space-y-6" data-testid="community-sidenav">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 px-3 mb-2">COMMUNITY</p>
        <nav className="space-y-0.5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={link} data-testid={`community-nav-${n.id}`}>
              <n.icon className="w-4 h-4" />{n.label}
              {n.id === "messages" && me?.unread_messages > 0 && <span className="ml-auto text-[10px] bg-cyan-400 text-slate-950 rounded-full px-1.5 font-bold" data-testid="community-unread-badge">{me.unread_messages}</span>}
              {n.id === "live" && liveTotal > 0 && <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-red-300 font-mono" data-testid="community-live-badge"><span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />{liveTotal}</span>}
            </NavLink>
          ))}
        </nav>
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 px-3 mb-2">YOUR GROUPS</p>
        <nav className="space-y-0.5">
          {groups.map((g) => (
            <NavLink key={g.slug} to={`/community/g/${g.slug}`} className={link} data-testid={`community-group-link-${g.slug}`}>
              {g.private ? <Lock className="w-3.5 h-3.5 text-amber-300" /> : <Globe className="w-3.5 h-3.5 text-cyan-400" />}
              <span className="truncate">{g.name}</span>
              {live[g.slug] > 0 && <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-red-300 font-mono" data-testid={`group-live-dot-${g.slug}`}><span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />{live[g.slug]}</span>}
              {g.pending && <span className="ml-auto text-[10px] text-amber-300">pending</span>}
              {!g.joined && !g.pending && !live[g.slug] && <span className="ml-auto text-[10px] text-slate-600">{g.private ? "request" : "join"}</span>}
            </NavLink>
          ))}
        </nav>
      </div>
    </aside>
  );
}

export default function CommunityLayout() {
  const { me, refresh } = useCommunity();
  const loc = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  return (
    <div className="min-h-screen bg-[#0B0D12] text-slate-100">
      <header className="border-b border-slate-800/80 bg-[#0B0D12]/90 backdrop-blur sticky top-16 z-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link to="/community" className="font-display font-extrabold tracking-tight text-lg" data-testid="community-brand">Velocity <span className="text-cyan-400">Community</span></Link>
          <form className="flex-1 max-w-md ml-4 hidden sm:block" onSubmit={(e) => { e.preventDefault(); navigate(`/community/search?q=${encodeURIComponent(q)}`); }}>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search posts, comments, groups…" data-testid="community-search" className="h-9 bg-[#131722] border-slate-800" />
          </form>
          <Link to={`/community${loc.pathname.includes("/g/") ? loc.pathname.replace("/community", "") : ""}?compose=1`} className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-cyan-400 text-slate-950 text-sm font-semibold hover:bg-cyan-300" data-testid="community-create-post">
            <Plus className="w-4 h-4" />Create post
          </Link>
        </div>
      </header>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[220px_1fr] gap-8">
        <div className="hidden lg:block"><SideNav me={me} /></div>
        <div className="lg:hidden -mx-4 px-4 overflow-x-auto flex gap-2 pb-1">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `whitespace-nowrap text-xs px-3 py-1.5 rounded-full border ${isActive ? "bg-cyan-400 text-slate-950 border-cyan-400" : "border-slate-800 text-slate-300"}`}>{n.label}</NavLink>)}
        </div>
        <main className="min-w-0"><Outlet context={{ me, refresh }} /></main>
      </div>
    </div>
  );
}
